import { useState, useRef, useCallback, useEffect } from 'react';

interface UseStickyScrollOptions {
  messagesEndRef: React.RefObject<HTMLDivElement | null>;
  messages: any[];
  showMessages?: boolean;
  isImageGenerating?: boolean;
}

export function useStickyScroll({
  messagesEndRef,
  messages,
  showMessages,
  isImageGenerating,
}: UseStickyScrollOptions) {
  const [isSticky, setIsSticky] = useState(true);
  const lastScrollTopRef = useRef(0);
  const isProgrammaticScrollRef = useRef(false);
  const isInitialMountRef = useRef(true);

  // Get the scrollable container
  const getScrollContainer = useCallback(() => {
    if (!messagesEndRef.current) return null;
    return messagesEndRef.current.closest('.overflow-y-auto') as HTMLElement;
  }, [messagesEndRef]);

  // Scroll to bottom function - scroll to full container bottom
  const scrollToBottom = useCallback((behavior: ScrollBehavior = 'smooth') => {
    const scrollContainer = getScrollContainer();
    if (!scrollContainer) return;
    
    isProgrammaticScrollRef.current = true;
    
    // Scroll to the very end of the container
    scrollContainer.scrollTo({
      top: scrollContainer.scrollHeight,
      behavior
    });
    
    setIsSticky(true); // Always enable sticky when scrolling to bottom
    
    // Reset the flag after a short delay to allow the scroll to complete
    setTimeout(() => {
      isProgrammaticScrollRef.current = false;
    }, 50);
  }, [getScrollContainer]);

  // Handle scroll events - cancel sticky on ANY manual scroll
  const handleScroll = useCallback(() => {
    // Ignore scroll events from programmatic scrolling
    if (isProgrammaticScrollRef.current) {
      return;
    }

    const scrollContainer = getScrollContainer();
    if (!scrollContainer) return;

    const { scrollTop } = scrollContainer;
    const lastScrollTop = lastScrollTopRef.current;
    
    // If user scrolled at all (up or down), disable sticky mode
    if (scrollTop !== lastScrollTop) {
      setIsSticky(false);
    }

    lastScrollTopRef.current = scrollTop;
  }, [getScrollContainer]);

  // Add scroll event listener
  useEffect(() => {
    const scrollContainer = getScrollContainer();
    if (!scrollContainer) return;

    scrollContainer.addEventListener('scroll', handleScroll);
    return () => {
      scrollContainer.removeEventListener('scroll', handleScroll);
    };
  }, [getScrollContainer, handleScroll]);

  // Auto-scroll when new messages arrive and we're in sticky mode
  useEffect(() => {
    if (isSticky && !isInitialMountRef.current) {
      scrollToBottom();
    }
  }, [messages, isSticky, scrollToBottom]);

  // Initial scroll to bottom on mount
  useEffect(() => {
    const timer = setTimeout(() => {
      scrollToBottom('auto');
      isInitialMountRef.current = false;
    }, 300);
    console.log('Initial scroll to bottom');
    return () => clearTimeout(timer);
  }, [scrollToBottom]);

  // Auto-scroll to bottom when showMessages becomes true (no animation)
  useEffect(() => {
    if (showMessages) {
      const timer = setTimeout(() => {
        scrollToBottom('auto');
      }, 300);
      console.log('Auto-scroll due to showMessages change');
      return () => clearTimeout(timer);
    }
  }, [showMessages, scrollToBottom]);

  // Auto-scroll to bottom when image generation starts (smooth animation)
  useEffect(() => {
    if (isImageGenerating) {
      setIsSticky(true); // Enable sticky mode
      scrollToBottom('smooth');
    }
  }, [isImageGenerating, scrollToBottom]);

  // Function to manually set sticky to true (for external triggers)
  const setStickyTrue = useCallback(() => {
    // console.log('🔄 Setting sticky to true due to message addition');
    setIsSticky(true);
  }, []);

  return {
    scrollToBottom,
    forceScrollToBottom: scrollToBottom, // Alias for backward compatibility
    isSticky,
    setStickyTrue,
  };
}